/**
 * TeamManagementPage - Full team management interface (DRR-061, DRR-055)
 *
 * Tab-based layout:
 * - Tab 1: Team List — Paginated table with search + type filter
 * - Tab 2: Org Chart — Hierarchy visualization (Phase 4)
 *
 * Uses useTeam() + TeamRepositoryV2 for all data access.
 * Create/Edit forms via EntityFormGenerator (backend-driven, DRR-055).
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  Title,
  Stack,
  Group,
  Button,
  Modal,
  Alert,
  Text,
  Badge,
  Skeleton,
  Paper,
  Tabs,
  Select,
  TextInput,
  Table,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconPlus,
  IconAlertCircle,
  IconUsers,
  IconSearch,
  IconHierarchy2,
  IconList,
  IconArrowsMove,
  IconGauge,
} from '@tabler/icons-react';

import { useTenantId } from '@/auth';
import { GqlTeamType } from '@/generated/graphql';
import { EntityFormGenerator, useTeam } from '@ferp/workflow-forms';
import type { UIContext, GqlTeam, CreateTeamInput } from '@ferp/workflow-forms';

import { TeamDetailDrawer } from './components/TeamDetailDrawer';
import { EditTeamModal } from './components/EditTeamModal';
import { ArchiveTeamModal } from './components/ArchiveTeamModal';
import { TeamMemberPanel } from './components/TeamMemberPanel';
import { TeamHierarchyView } from './components/TeamHierarchyView';

/** Parse tags from form value — handles both string and array inputs */
function parseTags(value: unknown): string[] | undefined {
  if (!value) return undefined;
  if (Array.isArray(value)) return (value as string[]).filter(Boolean);
  if (typeof value === 'string') return value.split(',').map((t) => t.trim()).filter(Boolean);
  return undefined;
}

// ============================================================================
// Constants
// ============================================================================

const TEAM_TYPE_COLORS: Record<string, string> = {
  [GqlTeamType.SQUAD]: 'cyan',
  [GqlTeamType.GROUP]: 'green',
  [GqlTeamType.DEPARTMENT]: 'blue',
  [GqlTeamType.DIVISION]: 'purple',
  [GqlTeamType.PROJECT]: 'orange',
  [GqlTeamType.CUSTOM]: 'gray',
};

const TEAM_TYPE_OPTIONS = Object.values(GqlTeamType).map((t) => ({
  value: t,
  label: t.charAt(0) + t.slice(1).toLowerCase(),
}));

// ============================================================================
// Component
// ============================================================================

export function TeamManagementPage() {
  const tenantId = useTenantId();
  const { repository } = useTeam();

  // Data state
  const [teams, setTeams] = useState<GqlTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutating, setMutating] = useState(false);

  // Filter state
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Selected team for detail/edit/archive/members
  const [selectedTeam, setSelectedTeam] = useState<GqlTeam | null>(null);

  // Modal/drawer states
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [archiveOpened, { open: openArchive, close: closeArchive }] = useDisclosure(false);
  const [membersOpened, { open: openMembers, close: closeMembers }] = useDisclosure(false);
  const [moveOpened, { open: openMove, close: closeMove }] = useDisclosure(false);
  const [quotasOpened, { open: openQuotas, close: closeQuotas }] = useDisclosure(false);

  // Move team form state
  const [newParentId, setNewParentId] = useState<string | null>(null);
  // Quotas form state
  const [quotaValues, setQuotaValues] = useState({ maxMembers: '', maxWorkflows: '', maxApiCallsPerDay: '', maxStorageGb: '' });

  // EntityFormGenerator context (DRR-055)
  const createContext: UIContext = useMemo(
    () => ({
      mode: 'CREATE' as const,
      tenantId: tenantId ?? '',
      safetyLevel: 'STANDARD',
    }),
    [tenantId],
  );

  // ========================================================================
  // Data Loading
  // ========================================================================

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      const result = await repository.listTeams();
      setTeams(result);
      setError(null);
    } catch {
      setError('Failed to load teams. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [repository]);

  useEffect(() => {
    if (tenantId) loadTeams();
  }, [tenantId, loadTeams]);

  // Filtered + searched teams
  const filteredTeams = useMemo(() => {
    let result = teams;
    if (typeFilter) result = result.filter((t) => t.teamType === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.code.toLowerCase().includes(q) ||
          (t.description?.toLowerCase().includes(q) ?? false),
      );
    }
    return result;
  }, [teams, typeFilter, search]);

  // ========================================================================
  // Handlers
  // ========================================================================

  const handleRowClick = useCallback(
    (team: GqlTeam) => {
      setSelectedTeam(team);
      openDetail();
    },
    [openDetail],
  );

  const handleCreate = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        setMutating(true);
        await repository.createTeam({
          name: values.name as string,
          code: values.code as string,
          teamType: values.teamType as CreateTeamInput['teamType'],
          description: (values.description as string) || undefined,
          parentTeamId: (values.parentTeamId as string) || undefined,
          costCenter: (values.costCenter as string) || undefined,
          effectiveDate: (values.effectiveDate as string) || undefined,
          budgetAllocated: values.budgetAllocated
            ? Number(values.budgetAllocated)
            : undefined,
          tags: parseTags(values.tags),
          teamLeadId: (values.teamLeadId as string) || undefined,
          deputyLeadId: (values.deputyLeadId as string) || undefined,
          contacts: {
            email: (values.contactEmail as string) || undefined,
            chatChannel: (values.contactChatChannel as string) || undefined,
            wikiUrl: (values.contactWikiUrl as string) || undefined,
            calendarUrl: (values.contactCalendarUrl as string) || undefined,
          },
        });
        closeCreate();
        loadTeams();
      } catch {
        // Apollo error state handles display
      } finally {
        setMutating(false);
      }
    },
    [repository, closeCreate, loadTeams],
  );

  const handleEdit = useCallback(
    async (values: Record<string, unknown>) => {
      if (!selectedTeam) return;
      try {
        setMutating(true);
        const updated = await repository.updateTeam(selectedTeam.code, {
          description: (values.description as string) || undefined,
          teamLeadId: (values.teamLeadId as string) || undefined,
          deputyLeadId: (values.deputyLeadId as string) || undefined,
          budgetAllocated: values.budgetAllocated
            ? Number(values.budgetAllocated)
            : undefined,
          costCenter: (values.costCenter as string) || undefined,
          tags: parseTags(values.tags),
          contacts: {
            email: (values.contactEmail as string) || undefined,
            chatChannel: (values.contactChatChannel as string) || undefined,
            wikiUrl: (values.contactWikiUrl as string) || undefined,
            calendarUrl: (values.contactCalendarUrl as string) || undefined,
          },
        });
        setSelectedTeam(updated);
        closeEdit();
        loadTeams();
      } catch {
        // Apollo error state handles display
      } finally {
        setMutating(false);
      }
    },
    [selectedTeam, repository, closeEdit, loadTeams],
  );

  const handleArchive = useCallback(
    async (code: string, reason: string) => {
      try {
        setMutating(true);
        await repository.archiveTeam(code, reason);
        closeArchive();
        closeDetail();
        setSelectedTeam(null);
        loadTeams();
      } catch {
        throw new Error('Failed to archive team');
      } finally {
        setMutating(false);
      }
    },
    [repository, closeArchive, closeDetail, loadTeams],
  );

  const handleAddStakeholder = useCallback(
    async (code: string, stakeholderId: string) => {
      try {
        setMutating(true);
        const updated = await repository.addStakeholder({ code, stakeholderId });
        setSelectedTeam(updated);
        loadTeams();
      } finally {
        setMutating(false);
      }
    },
    [repository, loadTeams],
  );

  const handleRemoveStakeholder = useCallback(
    async (code: string, stakeholderId: string) => {
      try {
        setMutating(true);
        const updated = await repository.removeStakeholder({ code, stakeholderId });
        setSelectedTeam(updated);
        loadTeams();
      } finally {
        setMutating(false);
      }
    },
    [repository, loadTeams],
  );

  const handleUpdateLead = useCallback(
    async (code: string, field: 'teamLeadId' | 'deputyLeadId', actorId: string | null) => {
      try {
        setMutating(true);
        const updated = await repository.updateTeam(code, { [field]: actorId ?? undefined });
        setSelectedTeam(updated);
        loadTeams();
      } finally {
        setMutating(false);
      }
    },
    [repository, loadTeams],
  );

  // Detail drawer action bridges
  const handleDetailEdit = useCallback(() => {
    openEdit();
  }, [openEdit]);

  const handleDetailArchive = useCallback(() => {
    openArchive();
  }, [openArchive]);

  const handleDetailMembers = useCallback(() => {
    openMembers();
  }, [openMembers]);

  const handleDetailMove = useCallback(() => {
    setNewParentId(selectedTeam?.parentTeamId ?? null);
    openMove();
  }, [openMove, selectedTeam]);

  const handleDetailQuotas = useCallback(() => {
    const q = selectedTeam?.quotaLimits;
    setQuotaValues({
      maxMembers: q?.maxMembers?.toString() ?? '',
      maxWorkflows: q?.maxWorkflows?.toString() ?? '',
      maxApiCallsPerDay: q?.maxApiCallsPerDay?.toString() ?? '',
      maxStorageGb: q?.maxStorageGb?.toString() ?? '',
    });
    openQuotas();
  }, [openQuotas, selectedTeam]);

  const handleMoveTeam = useCallback(async () => {
    if (!selectedTeam) return;
    try {
      setMutating(true);
      const updated = await repository.moveTeam({
        code: selectedTeam.code,
        newParentId: newParentId || undefined,
      });
      setSelectedTeam(updated);
      closeMove();
      loadTeams();
    } catch {
      setError('Failed to move team');
    } finally {
      setMutating(false);
    }
  }, [selectedTeam, newParentId, repository, closeMove, loadTeams]);

  const handleUpdateQuotas = useCallback(async () => {
    if (!selectedTeam) return;
    try {
      setMutating(true);
      const updated = await repository.updateTeamQuotas({
        code: selectedTeam.code,
        quotaLimits: {
          maxMembers: quotaValues.maxMembers ? Number(quotaValues.maxMembers) : undefined,
          maxWorkflows: quotaValues.maxWorkflows ? Number(quotaValues.maxWorkflows) : undefined,
          maxApiCallsPerDay: quotaValues.maxApiCallsPerDay ? Number(quotaValues.maxApiCallsPerDay) : undefined,
          maxStorageGb: quotaValues.maxStorageGb ? Number(quotaValues.maxStorageGb) : undefined,
        },
      });
      setSelectedTeam(updated);
      closeQuotas();
      loadTeams();
    } catch {
      setError('Failed to update quotas');
    } finally {
      setMutating(false);
    }
  }, [selectedTeam, quotaValues, repository, closeQuotas, loadTeams]);

  return (
    <Container size="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Team Management</Title>
            <Text c="dimmed" size="sm">
              Create and manage teams, assign members, view hierarchy
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Create Team
          </Button>
        </Group>

        {/* Error */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" variant="light">
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Group gap="sm">
          <TextInput
            placeholder="Search teams..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="All types"
            data={TEAM_TYPE_OPTIONS}
            value={typeFilter}
            onChange={setTypeFilter}
            clearable
            w={180}
          />
        </Group>

        {/* Tabs */}
        <Tabs defaultValue="list">
          <Tabs.List>
            <Tabs.Tab value="list" leftSection={<IconList size={14} />}>
              Team List ({filteredTeams.length})
            </Tabs.Tab>
            <Tabs.Tab value="orgchart" leftSection={<IconHierarchy2 size={14} />}>
              Org Chart
            </Tabs.Tab>
          </Tabs.List>

          {/* ============================================================ */}
          {/* Tab 1: Team List                                             */}
          {/* ============================================================ */}
          <Tabs.Panel value="list" pt="md">
            {loading ? (
              <Stack gap="xs">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} height={40} radius="sm" />
                ))}
              </Stack>
            ) : filteredTeams.length === 0 ? (
              <Paper p="xl" withBorder ta="center">
                <Stack gap="sm" align="center">
                  <IconUsers size={48} color="gray" />
                  <Text size="lg" fw={500}>
                    {teams.length === 0 ? 'No Teams Yet' : 'No Matching Teams'}
                  </Text>
                  <Text c="dimmed" size="sm">
                    {teams.length === 0
                      ? 'Create your first team to get started.'
                      : 'Try adjusting your search or filter.'}
                  </Text>
                  {teams.length === 0 && (
                    <Button leftSection={<IconPlus size={16} />} onClick={openCreate} mt="sm">
                      Create Team
                    </Button>
                  )}
                </Stack>
              </Paper>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Code</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Members</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Level</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredTeams.map((team) => (
                    <Table.Tr
                      key={team.id}
                      onClick={() => handleRowClick(team)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {team.name}
                        </Text>
                        {team.description && (
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {team.description}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace">
                          {team.code}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={TEAM_TYPE_COLORS[team.teamType] ?? 'gray'}
                          variant="light"
                          size="sm"
                        >
                          {team.teamType}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{team.stakeholderIds?.length ?? 0}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={team.active ? 'green' : 'red'}
                          variant="light"
                          size="xs"
                        >
                          {team.active ? 'Active' : 'Archived'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{team.level}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Tabs.Panel>

          {/* ============================================================ */}
          {/* Tab 2: Org Chart (Phase 4)                                   */}
          {/* ============================================================ */}
          <Tabs.Panel value="orgchart" pt="md">
            {loading ? (
              <Stack gap="xs">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} height={40} radius="sm" />
                ))}
              </Stack>
            ) : (
              <TeamHierarchyView teams={teams} onTeamClick={(teamId) => handleRowClick(teams.find((t) => t.id === teamId)!)} />
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* ================================================================ */}
      {/* Create Team Modal (DRR-055: Backend-driven form)                 */}
      {/* ================================================================ */}
      <Modal opened={createOpened} onClose={closeCreate} title="Create Team" size="lg">
        <Paper p="md">
          <EntityFormGenerator
            subgraph="core"
            entityType="Team"
            context={createContext}
            onSubmit={handleCreate}
            disabled={mutating}
            loading={mutating}
          />
        </Paper>
      </Modal>

      {/* ================================================================ */}
      {/* Team Detail Drawer                                               */}
      {/* ================================================================ */}
      <TeamDetailDrawer
        team={selectedTeam}
        opened={detailOpened}
        onClose={closeDetail}
        onEdit={handleDetailEdit}
        onArchive={handleDetailArchive}
        onManageMembers={handleDetailMembers}
        onMoveTeam={handleDetailMove}
        onUpdateQuotas={handleDetailQuotas}
      />

      {/* ================================================================ */}
      {/* Edit Team Modal                                                  */}
      {/* ================================================================ */}
      <EditTeamModal
        team={selectedTeam}
        opened={editOpened}
        onClose={closeEdit}
        onSubmit={handleEdit}
        loading={mutating}
      />

      {/* ================================================================ */}
      {/* Archive Team Modal                                               */}
      {/* ================================================================ */}
      <ArchiveTeamModal
        team={selectedTeam}
        opened={archiveOpened}
        onClose={closeArchive}
        onArchive={handleArchive}
        loading={mutating}
      />

      {/* ================================================================ */}
      {/* Members Modal                                                    */}
      {/* ================================================================ */}
      <Modal
        opened={membersOpened}
        onClose={closeMembers}
        title={selectedTeam ? `Manage Members: ${selectedTeam.name}` : 'Team Members'}
        size="lg"
      >
        {selectedTeam && (
          <TeamMemberPanel
            team={selectedTeam}
            onAddStakeholder={handleAddStakeholder}
            onRemoveStakeholder={handleRemoveStakeholder}
            onUpdateLead={handleUpdateLead}
            loading={mutating}
          />
        )}
      </Modal>

      {/* ================================================================ */}
      {/* Move Team Modal                                                  */}
      {/* ================================================================ */}
      <Modal
        opened={moveOpened}
        onClose={closeMove}
        title={selectedTeam ? `Move Team: ${selectedTeam.name}` : 'Move Team'}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Select a new parent team, or leave empty to make this a root team.
          </Text>
          <Select
            label="New Parent Team"
            placeholder="Select parent (empty = root)"
            data={teams
              .filter((t) => t.code !== selectedTeam?.code && t.active)
              .map((t) => ({ value: t.id, label: `${t.name} (${t.code})` }))}
            value={newParentId}
            onChange={setNewParentId}
            clearable
            searchable
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeMove}>Cancel</Button>
            <Button
              leftSection={<IconArrowsMove size={16} />}
              onClick={handleMoveTeam}
              loading={mutating}
            >
              Move Team
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ================================================================ */}
      {/* Update Quotas Modal                                              */}
      {/* ================================================================ */}
      <Modal
        opened={quotasOpened}
        onClose={closeQuotas}
        title={selectedTeam ? `Quotas: ${selectedTeam.name}` : 'Update Quotas'}
      >
        <Stack gap="md">
          <TextInput
            label="Max Members"
            type="number"
            value={quotaValues.maxMembers}
            onChange={(e) => setQuotaValues((v) => ({ ...v, maxMembers: e.currentTarget.value }))}
          />
          <TextInput
            label="Max Workflows"
            type="number"
            value={quotaValues.maxWorkflows}
            onChange={(e) => setQuotaValues((v) => ({ ...v, maxWorkflows: e.currentTarget.value }))}
          />
          <TextInput
            label="Max API Calls/Day"
            type="number"
            value={quotaValues.maxApiCallsPerDay}
            onChange={(e) => setQuotaValues((v) => ({ ...v, maxApiCallsPerDay: e.currentTarget.value }))}
          />
          <TextInput
            label="Max Storage (GB)"
            type="number"
            value={quotaValues.maxStorageGb}
            onChange={(e) => setQuotaValues((v) => ({ ...v, maxStorageGb: e.currentTarget.value }))}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeQuotas}>Cancel</Button>
            <Button
              leftSection={<IconGauge size={16} />}
              onClick={handleUpdateQuotas}
              loading={mutating}
            >
              Save Quotas
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
